/**
 * Границы административных единиц — напрямую из OSM Nominatim в рантайме
 * (решение: у себя границы не храним, см. обсуждение DR-01 §5).
 *
 * Nominatim public API: ~1 запрос/сек, CORS открыт. Запрос уходит только
 * по выбору КАТО в селекторе — в лимиты укладываемся.
 */
import type { GeoJsonObject } from 'geojson';

export interface OsmBoundary {
  /** Polygon / MultiPolygon границы. */
  geometry: GeoJsonObject;
  /** [[south, west], [north, east]] — для flyToBounds. */
  bbox: [[number, number], [number, number]];
}

interface NominatimResult {
  boundingbox: [string, string, string, string]; // [south, north, west, east]
  geojson?: { type: string } & GeoJsonObject;
  class?: string;
  type?: string;
}

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

/**
 * Варианты запроса границы по названию из справочника КАТО. В справочнике
 * тип единицы сокращён («Токушинский с.о.», «Петропавловск г.а.»), а OSM
 * ищет по полному имени («Токушинский сельский округ») — сокращённое не
 * находится вовсе. Часть округов размечена в OSM только по-казахски
 * («Арықбалық ауылдық округі»), поэтому вторым вариантом идёт kz-имя.
 */
export const osmBoundaryQueries = (
  nameRu: string,
  nameKz: string | null,
  oblastRu?: string,
): string[] => {
  const ru = nameRu
    .replace(/\s*с\.\s*о\.?\s*$/i, ' сельский округ')
    .replace(/\s*г\.\s*а\.?\s*$/i, '')
    .trim();
  const queries = [oblastRu ? `${ru}, ${oblastRu}` : ru];
  const kzOkrug = nameKz?.match(/^(.*?)\s*а\.\s*о\.?\s*$/i);
  if (kzOkrug) queries.push(`${kzOkrug[1].trim()} ауылдық округі`);
  return queries;
};

/**
 * Ищет полигон границы по названию единицы (ru), в пределах Казахстана.
 * Возвращает null, если в OSM нет полигона (для многих сельских округов
 * и СНП границы не размечены) — вызывающий код делает fallback на точку.
 *
 * `near` — [lat, lon] точки внутри искомой территории: одноимённые
 * единицы в других регионах отсеиваются по вхождению точки в bbox
 * результата (пример: «Жамбылский район» СКО против Жамбылской области
 * на юге). С `near` результат без вхождения точки → null (fallback-зум
 * по точкам поддерева — в правильном регионе).
 */
export const fetchOsmBoundary = async (
  query: string,
  near?: [number, number]
): Promise<OsmBoundary | null> => {
  const url = new URL(NOMINATIM_URL);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('polygon_geojson', '1');
  url.searchParams.set('limit', '5');
  url.searchParams.set('countrycodes', 'kz');
  url.searchParams.set('accept-language', 'ru');
  url.searchParams.set('q', query);

  const response = await fetch(url.toString(), {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    return null;
  }
  const results = (await response.json()) as NominatimResult[];
  if (!Array.isArray(results)) {
    return null;
  }

  // Только полигоны: точечные результаты (node) для зума по границе бесполезны.
  const polygonHits = results.filter(
    (r) =>
      r.geojson &&
      (r.geojson.type === 'Polygon' || r.geojson.type === 'MultiPolygon') &&
      Array.isArray(r.boundingbox)
  );

  const bboxOf = (
    r: NominatimResult
  ): [number, number, number, number] | null => {
    const [south, north, west, east] = r.boundingbox.map(Number);
    return [south, north, west, east].every(Number.isFinite)
      ? [south, north, west, east]
      : null;
  };

  const hit = near
    ? polygonHits.find((r) => {
        const box = bboxOf(r);
        return (
          box !== null &&
          near[0] >= box[0] &&
          near[0] <= box[1] &&
          near[1] >= box[2] &&
          near[1] <= box[3]
        );
      })
    : polygonHits[0];
  if (!hit?.geojson) {
    return null;
  }

  const box = bboxOf(hit);
  if (box === null) {
    return null;
  }
  const [south, north, west, east] = box;
  return {
    geometry: hit.geojson,
    bbox: [
      [south, west],
      [north, east],
    ],
  };
};
